import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '../Modal';
import {
  reminderError,
  useReminderPreview,
  useSaveReminderSettings,
} from '../../hooks/useCohortReminders';
import { CARD, PRIMARY, SECONDARY, previewTotals } from './reminderText';

const MAX_HOURS = 4;

const formatWhen = (value) =>
  value ? format(parseISO(value), 'd MMM yyyy, HH:mm') : '';

// Hours as entered, de-duplicated and longest first ("48h, then 24h").
const normaliseHours = (hours) =>
  [...new Set(hours.map(Number))].sort((a, b) => b - a);

function HoursEditor({ hours, onChange, problems }) {
  const [input, setInput] = useState('');
  const value = Number(input);
  const valid =
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 336 &&
    !hours.includes(value);
  const add = () => {
    if (!valid || hours.length >= MAX_HOURS) return;
    onChange(normaliseHours([...hours, value]));
    setInput('');
  };
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-700">
        Remind learners this many hours before the deadline
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {hours.map((h) => (
          <span
            key={h}
            className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-3 py-1 text-sm text-gray-800"
          >
            {h}h
            {hours.length > 1 && (
              <button
                type="button"
                aria-label={`Remove ${h} hours`}
                onClick={() => onChange(hours.filter((x) => x !== h))}
                className="text-gray-400 hover:text-gray-700"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </button>
            )}
          </span>
        ))}
        {hours.length < MAX_HOURS && (
          <span className="inline-flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={336}
              step={1}
              aria-label="Add reminder hours"
              placeholder="e.g. 72"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={add}
              disabled={!valid}
              className={SECONDARY}
            >
              Add
            </button>
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Up to {MAX_HOURS} reminders, 1–336 hours (14 days) before. The last one
        is the final reminder.
      </p>
      {problems.map((p) => (
        <p key={p.message} className="text-xs text-red-700">
          {p.message}
        </p>
      ))}
    </div>
  );
}

function EnableDialog({ open, cohortId, saving, onConfirm, onClose }) {
  const { data, isLoading, error } = useReminderPreview(cohortId, open);
  const totals = previewTotals(data);
  return (
    <Modal
      modalOpen={open}
      onClose={onClose}
      title="Turn on assignment reminders?"
      small
    >
      <div className="space-y-3 text-left text-sm text-gray-700">
        <p>
          Learners who haven&apos;t submitted will be reminded before each
          deadline. Only reminders due from now on are sent; nothing earlier is
          sent late.
        </p>
        {isLoading && (
          <p className="text-gray-500">Checking what would go out…</p>
        )}
        {error && (
          <p className="text-red-700">
            {reminderError(error, "We couldn't load the preview.")}
          </p>
        )}
        {data && (
          <p>
            In the next 72 hours that&apos;s {totals.reminders} reminder
            {totals.reminders === 1 ? '' : 's'}: {totals.inApp.toLocaleString()}{' '}
            in-app, {totals.email.toLocaleString()} email and{' '}
            {(data.sms_messages ?? 0).toLocaleString()} SMS
            {data.sms_cost_kes !== undefined && data.sms_cost_kes !== null
              ? ` (about KES ${data.sms_cost_kes})`
              : ''}
            .
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className={PRIMARY}
          >
            {saving ? 'Turning on…' : 'Turn on'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function ReminderSettings({ cohortId, data }) {
  const save = useSaveReminderSettings(cohortId);
  const saved = data.settings;
  const baseline = useMemo(
    () => ({
      reminder_hours: normaliseHours(saved.reminder_hours ?? [48, 24]),
      sms_on_final: Boolean(saved.sms_on_final),
      missed_notice: Boolean(saved.missed_notice),
    }),
    [saved]
  );
  const [draft, setDraft] = useState(baseline);
  const [problems, setProblems] = useState([]);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => setDraft(baseline), [baseline]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  const on = saved.assignment_reminders_enabled;

  const put = (changes, success) =>
    save.mutate(changes, {
      onSuccess: () => {
        setProblems([]);
        setConfirm(null);
        toast.success(success);
      },
      onError: (error) => {
        setConfirm(null);
        setProblems(error?.response?.data?.details?.problems ?? []);
        toast.error(
          reminderError(error, "We couldn't save the reminder settings.")
        );
      },
    });

  const fieldProblems = (field) =>
    problems.filter((p) => (p.field || '').startsWith(field));
  const otherProblems = problems.filter(
    (p) =>
      !['reminder_hours', 'sms_on_final', 'missed_notice'].some((f) =>
        (p.field || '').startsWith(f)
      )
  );

  return (
    <section className={`${CARD} space-y-5 p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                on ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {on ? 'On' : 'Off'}
            </span>
            <p className="text-sm text-gray-900">
              {on
                ? 'Learners who haven’t submitted are reminded before each deadline.'
                : 'Off: no assignment reminders are sent for this cohort.'}
            </p>
          </div>
          <p className="text-xs text-gray-500">
            {on &&
              saved.enabled_at &&
              `On since ${formatWhen(saved.enabled_at)}. `}
            {data.updated_at &&
              `Last changed ${formatWhen(data.updated_at)}${
                data.updated_by ? ` by ${data.updated_by}` : ''
              }.`}
          </p>
        </div>
        {on ? (
          <button
            type="button"
            onClick={() => setConfirm('off')}
            disabled={save.isLoading}
            className={SECONDARY}
          >
            Turn off…
          </button>
        ) : (
          <div className="text-right">
            <button
              type="button"
              onClick={() => setConfirm('on')}
              disabled={dirty || save.isLoading}
              className={PRIMARY}
            >
              Turn on…
            </button>
            {dirty && (
              <p className="mt-1 text-[11px] text-gray-500">
                Save your changes first.
              </p>
            )}
          </div>
        )}
      </div>

      <HoursEditor
        hours={draft.reminder_hours}
        onChange={(reminder_hours) =>
          setDraft((d) => ({ ...d, reminder_hours }))
        }
        problems={fieldProblems('reminder_hours')}
      />

      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-1"
          checked={draft.sms_on_final && data.sms_available !== false}
          disabled={data.sms_available === false}
          onChange={(e) =>
            setDraft((d) => ({ ...d, sms_on_final: e.target.checked }))
          }
        />
        <span>
          Also send the final reminder by SMS
          <span className="block text-xs text-gray-500">
            {data.sms_available === false
              ? 'SMS isn’t set up on the server, so it’s unavailable.'
              : 'Kenyan mobile numbers only; learners can turn SMS reminders off.'}
          </span>
        </span>
      </label>

      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-1"
          checked={draft.missed_notice}
          onChange={(e) =>
            setDraft((d) => ({ ...d, missed_notice: e.target.checked }))
          }
        />
        <span>
          Tell learners when they&apos;ve missed a deadline
          <span className="block text-xs text-gray-500">
            In-app and email, within 6 hours after the deadline.
          </span>
        </span>
      </label>

      {otherProblems.map((p) => (
        <p key={p.message} className="text-xs text-red-700">
          {p.message}
        </p>
      ))}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => put(draft, 'Reminder settings saved.')}
          disabled={!dirty || save.isLoading}
          className={PRIMARY}
        >
          {save.isLoading && !confirm ? 'Saving…' : 'Save settings'}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(baseline)}
            className={SECONDARY}
          >
            Discard
          </button>
        )}
      </div>

      <EnableDialog
        open={confirm === 'on'}
        cohortId={cohortId}
        saving={save.isLoading}
        onConfirm={() =>
          put(
            { assignment_reminders_enabled: true },
            'Assignment reminders are on.'
          )
        }
        onClose={() => setConfirm(null)}
      />
      <Modal
        modalOpen={confirm === 'off'}
        onClose={() => setConfirm(null)}
        title="Turn off assignment reminders?"
        small
      >
        <div className="space-y-3 text-left text-sm text-gray-700">
          <p>
            No more reminders or missed-deadline notices go out for this cohort.
            Settings are kept.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className={SECONDARY}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                put(
                  { assignment_reminders_enabled: false },
                  'Assignment reminders are off.'
                )
              }
              disabled={save.isLoading}
              className={PRIMARY}
            >
              Turn off
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
